import {usePromotion} from '@/context/Promotion/PromotionContext'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {DiscountReward, PromotionCondition, Reward, RewardType, rewardTypeToIdMap} from '@/types/promotion'
import {COUNTRY_CODES} from '@/utils/constants/countryConstants'
import {PromotionType, RewardTypes} from '@/utils/constants/promotionConstants'
import {useSelector} from 'react-redux'

type RewardOption = {id: string; rewardType?: string; discountType?: string}

type UseRewardItemArgs = {
  promotionCondition: PromotionCondition
  rewardIndex?: number
}

export const useRewardItem = ({promotionCondition, rewardIndex = 0}: UseRewardItemArgs) => {
  const {promotion, setMaxDiscountModalVisible, onResultChange, promotionConfig, updatePromotion} = usePromotion()
  const countryCode = useSelector(selectCountryCode)
  const {maxDiscountPercentage, conditions} = promotionConfig
  const configuredRewardOptions = conditions?.rewardTypeOptions?.map(option => ({
    displayText: option.value === RewardTypes.FREE_PRODUCT_FROM_LIST ? 'Free Product from lists' : option.label,
    id: option.value,
    discountType: option.discountType || option.rewardType,
    rewardType: option.rewardType,
  }))
  const hasMinQuantity = promotion?.promotionConditions?.[0]?.conditionGroup?.conditions?.[0]?.hasMinQuantity
  const canUseFreeProductFromList =
    promotion?.promotionType === PromotionType.BUY_PRODUCT && countryCode === COUNTRY_CODES.TR && !hasMinQuantity
  const rewardOptions = configuredRewardOptions?.filter(
    option => canUseFreeProductFromList || option.id !== RewardTypes.FREE_PRODUCT_FROM_LIST
  )
  if (
    canUseFreeProductFromList &&
    rewardOptions &&
    !rewardOptions.some(option => option.id === RewardTypes.FREE_PRODUCT_FROM_LIST)
  ) {
    rewardOptions.push({
      displayText: 'Free Product from lists',
      id: RewardTypes.FREE_PRODUCT_FROM_LIST,
      discountType: RewardTypes.FREE_PRODUCT,
      rewardType: RewardTypes.FREE_PRODUCT,
    })
  }

  const selectedReward = promotion?.promotionConditions?.[0]?.rewards?.[rewardIndex]
  const selectedDiscountType = selectedReward && 'discountType' in selectedReward ? selectedReward.discountType : ''
  const rewardType =
    rewardTypeToIdMap?.[selectedReward?.type || '']?.[selectedDiscountType || ''] || selectedReward?.type || ''
  const rewardValue = rewardOptions?.filter(item => item.id === rewardType)

  const handleDeleteLayer = () => {
    const updatedConditions = promotion?.promotionConditions?.filter(
      condition => condition.sequence !== promotionCondition?.sequence
    )
    onResultChange({promotionConditions: updatedConditions})
  }

  const handleSelectedRewardItem = (value: RewardOption[]) => {
    if (!value || !value.length) return
    const selected = value[0]
    const isFreeProductFromList = selected.id === RewardTypes.FREE_PRODUCT_FROM_LIST

    updatePromotion(prev => ({
      ...prev,
      promotionConditions: (isFreeProductFromList
        ? prev?.promotionConditions?.slice(0, 1)
        : prev?.promotionConditions
      )?.map(condition => {
        // Individual rewards must all share the first reward's type, so every reward of the layer follows it
        const rewards = condition?.rewards ?? []
        const newReward = (
          isFreeProductFromList
            ? {
                type: RewardType.FREE_PRODUCT_FROM_LIST,
                mandatoryProductsEnabled: true,
                freeProductLists: [{groupName: 'List A', items: []}],
              }
            : {type: selected.rewardType ?? selected.id, items: [], discountType: selected.discountType}
        ) as Reward
        return {
          ...condition,
          ...(isFreeProductFromList && {
            conditionGroup: {
              ...condition.conditionGroup,
              conditions: (condition.conditionGroup?.conditions ?? []).map((buyCondition, index) =>
                index === 0
                  ? {
                      ...buyCondition,
                      hasMinQuantity: false,
                      minQuantity: null,
                      minItemType: undefined,
                      minItems: [],
                    }
                  : buyCondition
              ),
            },
          }),
          rewards: Array.from({length: Math.max(rewards.length, 1)}, () => newReward),
        }
      }),
    }))
  }

  const handleEditMaxDiscount = () => setMaxDiscountModalVisible(true)

  const discountPercentage = (promotionCondition?.rewards?.[rewardIndex] as DiscountReward)?.discountValue || ''
  const isMaxDiscountExceeded =
    rewardType === RewardTypes.PERCENTAGE_DISCOUNT && discountPercentage > maxDiscountPercentage
  const canDeleteLayer = promotionCondition?.sequence > 1

  return {
    rewardOptions,
    rewardType,
    rewardValue,
    selectedDiscountType: rewardValue?.[0]?.discountType,
    isMaxDiscountExceeded,
    canDeleteLayer,
    handleSelectedRewardItem,
    handleDeleteLayer,
    handleEditMaxDiscount,
  }
}
